import React, { useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import FilterBar from './filter-search/filter-bar';
import './search-bar.scss';
import { useDebounce } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';
import { AccountingNumberDto } from '../../../models/accounting-number.dto';
import { CostCenterDto } from '../../../models/cost-center.dto';
import { ReactComponent as FilterIcon } from '../../../assets/icons/filter.svg';
import { Search } from 'react-bootstrap-icons';
import {
   INVOICE_GENERATION_FILER_ALL,
   INVOICE_GENERATION_FILER_GENERATED,
   INVOICE_GENERATION_FILER_PENDING, INVOICE_PAGINATION_DEFAULT_SIZE,
   InvoiceGenerationFilters,
} from '../../../models/invoice.dto';

interface PropsType {
   onSearch: (
      name: string,
      fromDate: Date,
      toDate: Date,
      fromAmount: string,
      toAmount: string,
      costCenter: string,
      accountingNumber: string,
      areExportedInvoicesShown: InvoiceGenerationFilters,
      page: number,
      size: number
   ) => void;
   searchParams;
   accountingNumbers: AccountingNumberDto[];
   costCenters: CostCenterDto[];
   generationDateFilter: InvoiceGenerationFilters;
   setGenerationDateFilter: (filter: InvoiceGenerationFilters) => void;
}

const SearchBar = ({
   onSearch,
   searchParams,
   accountingNumbers,
   costCenters,
   generationDateFilter,
   setGenerationDateFilter,
}: PropsType) => {
   const [translate] = useTranslation(['common']);
   const searchPlaceholder = translate('search.searchHere');

   const [fromDate, setFromDate] = useState<Date>(new Date(searchParams.fromDate));
   const [toDate, setToDate] = useState<Date>(new Date(searchParams.toDate));
   const [fromAmount, setFromAmount] = useState<string>(searchParams.fromAmount);
   const [toAmount, setToAmount] = useState<string>(searchParams.toAmount);
   const [name, setName] = useState<string>(searchParams.name);
   const [accountingNumber, setAccountingNumber] = useState<string>(
      searchParams.accountingNumberValue,
   );
   const [costCenter, setCostCenter] = useState<string>(searchParams.costCenterValue);
   const debouncedFromAmount = useDebounce<string>(fromAmount, 500);
   const debouncedToAmount = useDebounce<string>(toAmount, 500);
   const debouncedName = useDebounce<string>(name, 500);

   const [hideFilter, setHideFilter] = useState<boolean>(false);
   const isInitialMount = useRef(true);

   useEffect(() => {
      if (isInitialMount.current) {
         isInitialMount.current = false;
      } else {
         onSearch(
            debouncedName,
            fromDate,
            toDate,
            fromAmount,
            toAmount,
            costCenter,
            accountingNumber,
            generationDateFilter,
            0,
            INVOICE_PAGINATION_DEFAULT_SIZE
         );
      }
   }, [
      debouncedName,
      fromDate,
      toDate,
      debouncedFromAmount,
      debouncedToAmount,
      costCenter,
      accountingNumber,
      generationDateFilter,
   ]);

   const onChangeFilterBar = (
      fromDate,
      toDate,
      fromAmount,
      toAmount,
      accountingNumber,
      costCenter,
   ) => {
      setFromDate(fromDate);
      setToDate(toDate);
      setFromAmount(fromAmount);
      setToAmount(toAmount);
      setAccountingNumber(accountingNumber);
      setCostCenter(costCenter);
   };

   function resetFilterValues(): void {
      setFromDate(new Date(searchParams.fromDate));
      setToDate(new Date(searchParams.toDate));
      setFromAmount(searchParams.fromAmount);
      setToAmount(searchParams.toAmount);
      setAccountingNumber(searchParams.accountingNumberValue);
      setCostCenter(searchParams.accountingNumberValue);
      setName(searchParams.name);
   }


   return (
      <>
         <div className='search-block'>
            <div className='invoice-tabs' data-tour='invoice-tabs'>
               <Button
                  className={`mb-1 top ${generationDateFilter === INVOICE_GENERATION_FILER_ALL ? 'selected-tab' : 'unSelected'}`}
                  onClick={() => setGenerationDateFilter(INVOICE_GENERATION_FILER_ALL)}
               >
                  {translate('common:common.all')}
               </Button>
               <Button
                  className={`mb-1 top ${generationDateFilter === INVOICE_GENERATION_FILER_PENDING ? 'selected-tab' : 'unSelected'}`}
                  onClick={() => setGenerationDateFilter(INVOICE_GENERATION_FILER_PENDING)}
               >
                  {translate('common:common.unpaid')}
               </Button>
               <Button
                  className={`mb-1 top ${generationDateFilter === INVOICE_GENERATION_FILER_GENERATED ? 'selected-tab' : 'unSelected'}`}
                  onClick={() => setGenerationDateFilter(INVOICE_GENERATION_FILER_GENERATED)}
               >
                  {translate('common:common.paid')}
               </Button>
            </div>

            <div className='search-container' >
               <Form
                  className='invoice-search-form'
                  onSubmit={(e) => {
                     e.preventDefault();
                  }}
               >
                  <Search className='invoice-search-icon' aria-hidden='true' />
                  <Form.Control
                     type='search'
                     value={name}
                     placeholder={searchPlaceholder}
                     onChange={(e) => {
                        setName(e?.target.value);
                     }}
                  />
               </Form>


               <Button
                  className={'mb-1 mx-1 top filter'}
                  onClick={() => {
                     setHideFilter(!hideFilter);
                  }}
               >
                  <FilterIcon className='filter-icon' />
                  {translate('common:common.filter')}
               </Button>
            </div>
         </div>

         <div id='filter' className={`box ${hideFilter ? '' : 'd-none'} w-100`}>
            <FilterBar
               fromDate={fromDate}
               toDate={toDate}
               fromAmount={fromAmount}
               toAmount={toAmount}
               costCenterValue={costCenter}
               accountingNumberValue={accountingNumber}
               onChangeFilterBar={onChangeFilterBar}
               accountingNumbers={accountingNumbers}
               costCenters={costCenters}
               resetFilterValues={resetFilterValues}
               onClose={() => setHideFilter(false)}
            />
         </div>
      </>
   );
};

export default SearchBar;
